import { Badge } from "../ui/badge"
import { Button } from "../ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar"
import { Link } from "react-router-dom"
import { Calendar, Clock, Users, Video, ExternalLink, CheckCircle2, ArrowRight, Loader2 } from "lucide-react"
import { formatDate, formatTime } from "../../utils/formatters"
import { useAuth } from "../../hooks/useAuth"

export default function WebinarListItem({ 
  webinar, 
  onRegister,
  onCancelRegistration,
  isRegistering = false,
  isCancelling = false
}) {
  const { isAuthenticated } = useAuth()

  const getStatusBadge = () => {
    switch (webinar?.status) {
      case 'UPCOMING':
        return { text: 'À venir', className: 'bg-[#203a8a] text-white' }
      case 'ONGOING':
        return { text: 'En cours', className: 'bg-[#E8C547] text-[#203a8a]' }
      case 'COMPLETED':
        return { text: 'Terminé', className: 'bg-[#6B7280] text-white' }
      default:
        return { text: 'Non défini', className: 'bg-[#9CA3AF] text-white' }
    }
  }

  const statusBadge = getStatusBadge()
  const webinarDate = webinar?.date ? new Date(webinar.date) : null
  const isUpcoming = webinar?.status === 'UPCOMING'
  const isOngoing = webinar?.status === 'ONGOING'

  return (
    <div className="bg-white border border-[#D5DAE3] overflow-hidden">
      <div className="flex flex-col md:flex-row">
        {/* Image */}
        <div className="relative w-full md:w-64 h-48 md:h-auto flex-shrink-0">
          <img
            src={webinar?.thumbnail || "/placeholder.svg"}
            alt={webinar?.title}
            className="w-full h-full object-cover"
          />
          {/* Badge statut */}
          <div className="absolute top-3 left-3">
            <Badge className={`text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 font-semibold ${statusBadge.className}`}>
              {statusBadge.text}
            </Badge>
          </div>
          {/* Badge inscription si inscrit */}
          {isAuthenticated && webinar?.isRegistered && (
            <div className="absolute top-3 right-3">
              <Badge className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 bg-[#E8C547] text-[#203a8a] font-semibold flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Inscrit
              </Badge>
            </div>
          )}
          {/* Badge brouillon si admin */}
          {!webinar?.isPublished && (
            <div className="absolute top-3 right-3">
              <Badge className="text-[10px] uppercase tracking-[0.22em] px-2 py-0.5 bg-[#9CA3AF] text-white font-semibold">
                Brouillon
              </Badge>
            </div>
          )}
        </div>

        {/* Contenu */}
        <div className="flex-1 p-6 flex flex-col">
          {/* En-tête */}
          <div className="mb-4">
            <h3 className="font-display text-xl text-[#1B2240] mb-2 line-clamp-2">
              {webinar?.title}
            </h3>
            <p className="font-sans text-[#6B7280] text-sm line-clamp-2">
              {webinar?.description || "Description non disponible"}
            </p>
          </div>

          {/* Informations */}
          <div className="flex flex-wrap gap-4 mb-4 text-sm text-[#6B7280]">
            {/* Date et heure */}
            {webinarDate && (
              <div className="flex items-center space-x-1">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(webinarDate)}</span>
                <span>•</span>
                <span>{formatTime(webinarDate)}</span>
              </div>
            )}

            {/* Durée */}
            {webinar?.duration && (
              <div className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>{webinar.duration} min</span>
              </div>
            )}

            {/* Intervenants */}
            {webinar?.speakers && Array.isArray(webinar.speakers) && webinar.speakers.length > 0 && (
              <div className="flex items-center space-x-2 flex-wrap">
                {webinar.speakers.slice(0, 2).map((speaker) => (
                  <div key={speaker.id} className="flex items-center space-x-1">
                    <Avatar className="w-5 h-5">
                      <AvatarImage src={speaker.photo} alt="Photo intervenant" />
                      <AvatarFallback className="text-xs">
                        {speaker.name
                          ? speaker.name.split(" ").map((n) => n[0]).join("")
                          : "SP"}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs">
                      {speaker.name}
                      {speaker.title && ` - ${speaker.title}`}
                    </span>
                  </div>
                ))}
                {webinar.speakers.length > 2 && (
                  <span className="text-xs text-gray-500">
                    +{webinar.speakers.length - 2} autre{webinar.speakers.length - 2 > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Lien vidéo replay si disponible */}
          {webinar?.videoLink && webinar?.status === 'COMPLETED' && (
            <div className="mb-4">
              <a 
                href={webinar.videoLink} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-1 text-sm text-[#203a8a] hover:text-[#E8C547] transition-colors"
              >
                <Video className="w-4 h-4" />
                <span>Voir le replay</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 mt-auto">
            <Button 
              className="bg-[#203a8a] text-white hover:bg-[#1a2f6e] text-xs uppercase tracking-[0.22em] font-semibold" 
              asChild
            >
              <Link to={`/webinars/${webinar?.id}`}>
                Voir les détails
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            
            {/* Bouton inscription/annulation si webinaire à venir */}
            {isUpcoming && webinar?.isPublished && (
              <>
                {isAuthenticated && webinar?.isRegistered ? (
                  <Button 
                    variant="outline"
                    onClick={() => onCancelRegistration?.(webinar.id)}
                    disabled={isCancelling}
                    className="text-xs uppercase tracking-[0.22em] border border-[#D5DAE3] text-[#6B7280] hover:border-[#203a8a] hover:text-[#203a8a] font-semibold"
                  >
                    {isCancelling ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Annulation...
                      </>
                    ) : (
                      "Annuler l'inscription"
                    )}
                  </Button>
                ) : !webinar?.isRegistered && (
                  <Button 
                    onClick={() => {
                      if (!isAuthenticated) {
                        // Rediriger vers la page de connexion si l'utilisateur n'est pas connecté
                        window.location.href = `/login?redirect=${encodeURIComponent(`/webinars/${webinar.id}`)}`
                        return
                      }
                      onRegister?.(webinar.id)
                    }}
                    className="bg-[#E8C547] text-[#203a8a] hover:bg-[#d4af3a] text-xs uppercase tracking-[0.22em] font-semibold"
                    disabled={(webinar?.maxParticipants && webinar.registrationsCount >= webinar.maxParticipants) || isRegistering}
                  >
                    {isRegistering ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Inscription...
                      </>
                    ) : (
                      "S'inscrire"
                    )}
                  </Button>
                )}
              </>
            )}

            {/* Bouton rejoindre si en cours */}
            {isOngoing && webinar?.meetLink && (
              <Button 
                asChild
                className="bg-[#203a8a] text-white hover:bg-[#1a2f6e] text-xs uppercase tracking-[0.22em] font-semibold"
              >
                <a
                  href={webinar.meetLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" />
                  Rejoindre la réunion
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

